'use client';

import React, { useState } from 'react';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';
import PrizmaLogo from '@/components/PrizmaLogo';
import Link from 'next/link';

export default function AuthClient() {
  const [tab, setTab] = useState<'login' | 'signup'>('login');

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col justify-between p-12" style={{ backgroundColor: '#0a0a0a' }}>
        <div
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden"
        >
          <span
            className="font-extrabold uppercase text-foreground"
            style={{
              fontSize: 'clamp(8rem, 18vw, 22rem)',
              letterSpacing: '-0.08em',
              opacity: 0.04,
              lineHeight: 1,
              whiteSpace: 'nowrap',
              transform: 'rotate(-10deg)',
            }}
          >
            PRIZMA
          </span>
        </div>

        <Link href="/">
          <PrizmaLogo size={40} showTagline />
        </Link>

        <div className="relative z-10 space-y-6">
          <h2
            className="font-extrabold text-foreground uppercase leading-none"
            style={{ fontSize: 'clamp(2.5rem, 5vw, 5rem)', letterSpacing: '-0.05em' }}
          >
            Everything,<br />
            <span style={{ color: 'var(--accent)' }}>In Every</span><br />
            Color.
          </h2>
          <p className="text-muted-foreground text-base leading-relaxed max-w-sm">
            Join 98,000+ shoppers discovering premium products across every category — curated, authentic, delivered.
          </p>

          {/* Rainbow bar */}
          <div className="flex gap-1 mt-8">
            {['#4A90D9', '#4DBFBF', '#7BC67E', '#B5CC4A', '#E8964D', '#E85D5D', '#D45CAC'].map(color => (
              <div
                key={color}
                className="h-1 flex-1 rounded-full"
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>

        <p className="text-xs text-muted-foreground">© 2026 Prizma. All rights reserved.</p>
      </div>

      {/* Right panel — auth form */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 py-12 overflow-y-auto">
        {/* Mobile logo */}
        <div className="lg:hidden mb-8">
          <Link href="/">
            <PrizmaLogo size={36} showTagline />
          </Link>
        </div>

        <div className="w-full max-w-md">
          {/* Tabs */}
          <div className="flex gap-1 p-1 rounded-xl bg-muted mb-8">
            {(['login', 'signup'] as const).map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold capitalize transition-all"
                style={{
                  backgroundColor: tab === t ? 'var(--card)' : 'transparent',
                  color: tab === t ? 'var(--foreground)' : 'var(--muted-foreground)',
                  boxShadow: tab === t ? '0 1px 4px rgba(0,0,0,0.3)' : 'none',
                }}
              >
                {t === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {tab === 'login' ? (
            <LoginForm onSwitchToSignup={() => setTab('signup')} />
          ) : (
            <SignupForm onSwitchToLogin={() => setTab('login')} />
          )}
        </div>
      </div>
    </div>
  );
}